import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  IconButton,
  Badge,
  Popover,
  Box,
  Typography,
  List,
  ListItemButton,
  ListItemText,
  CircularProgress,
  Pagination,
  Divider,
} from "@mui/material";
import NotificationsIcon from "@mui/icons-material/Notifications";
import {
  getUserNotifications,
  getUnreadNotificationCount,
  markNotificationAsRead,
} from "../../services/apiService";
import { formatDateToDDMMYYYYWithTime12Hr } from "../../utils/dateFormat";

const PAGE_LIMIT = 10;

// Notifications are shown to any authenticated user — this includes Reviewers whose
// application role may be neither Admin nor Contributor. A user who never receives
// notifications (e.g. via reviewer_id assignment) simply sees an empty/zero-count bell.
function NotificationBell({ sx }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const userId = localStorage.getItem("id");

  const refreshUnreadCount = useCallback(async () => {
    const { data } = await getUnreadNotificationCount();
    if (typeof data?.data === "number") {
      setUnreadCount(data.data);
    }
  }, []);

  useEffect(() => {
    if (!userId) return;
    refreshUnreadCount();
    const interval = setInterval(refreshUnreadCount, 60000);
    return () => clearInterval(interval);
  }, [userId, refreshUnreadCount]);

  const fetchNotifications = useCallback(async (pageToLoad) => {
    setLoading(true);
    const { data } = await getUserNotifications(pageToLoad, PAGE_LIMIT);
    if (data) {
      setNotifications(data.data || []);
      setTotalCount(data.totalCount || 0);
      if (typeof data.unreadCount === "number") {
        setUnreadCount(data.unreadCount);
      }
    }
    setLoading(false);
  }, []);

  const handleOpen = (event) => {
    setAnchorEl(event.currentTarget);
    setPage(1);
    fetchNotifications(1);
  };

  const handleClose = () => setAnchorEl(null);

  const handlePageChange = (_e, value) => {
    setPage(value);
    fetchNotifications(value);
  };

  const handleNotificationClick = async (notification) => {
    if (!notification.isRead) {
      await markNotificationAsRead(notification.id);
      setNotifications((prev) => prev.filter((n) => n.id !== notification.id));
      refreshUnreadCount();
    }

    handleClose();
    navigate(`/asset/${notification.assetId}`, {
      state: {
        highlightReviewId: notification.assetReviewId,
        focusInlineCommentId: notification.inlineCommentId,
      },
    });
  };

  if (!userId) {
    return null;
  }

  const open = Boolean(anchorEl);
  const pageCount = Math.max(1, Math.ceil(totalCount / PAGE_LIMIT));

  return (
    <>
      <IconButton onClick={handleOpen} color="inherit" aria-label="notifications" sx={sx}>
        <Badge badgeContent={unreadCount} color="error">
          <NotificationsIcon />
        </Badge>
      </IconButton>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Box sx={{ width: 380, maxHeight: 480, display: "flex", flexDirection: "column" }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, p: 2, pb: 1 }}>
            Notifications
          </Typography>
          <Divider />

          <Box sx={{ flex: 1, overflowY: "auto" }}>
            {loading && (
              <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                <CircularProgress size={22} />
              </Box>
            )}

            {!loading && notifications.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                No notifications.
              </Typography>
            )}

            {!loading && notifications.length > 0 && (
              <List disablePadding>
                {notifications.map((notification) => (
                  <ListItemButton
                    key={notification.id}
                    onClick={() => handleNotificationClick(notification)}
                    sx={{
                      alignItems: "flex-start",
                      backgroundColor: notification.isRead ? "transparent" : "action.hover",
                      borderBottom: "1px solid",
                      borderColor: "divider",
                    }}
                  >
                    <ListItemText
                      primary={notification.message}
                      primaryTypographyProps={{
                        variant: "body2",
                        sx: { fontWeight: notification.isRead ? 400 : 600, whiteSpace: "pre-line" },
                      }}
                      secondary={formatDateToDDMMYYYYWithTime12Hr(notification.createdDate)}
                      secondaryTypographyProps={{ variant: "caption" }}
                    />
                  </ListItemButton>
                ))}
              </List>
            )}
          </Box>

          {pageCount > 1 && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 1.5 }}>
              <Pagination count={pageCount} page={page} onChange={handlePageChange} size="small" />
            </Box>
          )}
        </Box>
      </Popover>
    </>
  );
}

export default NotificationBell;
